import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowRight, Radar } from 'lucide-react';
import { useStore } from '../../store/StoreContext';
import { ScreenHeader } from '../../components/ui/ScreenHeader';
import { SegmentedControl } from '../../components/ui/SegmentedControl';
import { AppCard } from '../../components/ui/AppCard';
import { EmptyState } from '../../components/ui/EmptyState';
import {
  fetchChannelInsights,
  type ChannelInsights,
  type InsightAnomaly,
  type InsightCorrelation,
  type InsightDirection,
  type InsightPrediction,
  type InsightSeverity,
} from '../../api/dataApi';

// The anomaly detection -> correlations -> predictions stage of the
// pipeline (raw data -> signals -> anomaly detection -> correlations ->
// predictions -> AI report), read off GET /stats/channel/{n}/insights
// (backend internal/insight, computed from the same chartroll rollups the
// Charts tab already reads). Every number here comes straight from that
// response - no invented alert thresholds, no fabricated confidence: a
// metric with too little history or too weak a trend is simply absent
// from its list.
const SEVERITY_STYLE: Record<InsightSeverity, { label: string; badge: string; bar: string }> = {
  high: { label: 'High', badge: 'border-red-500/30 bg-red-500/10 text-red-600 dark:text-red-400', bar: 'bg-red-500' },
  medium: { label: 'Medium', badge: 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400', bar: 'bg-amber-500' },
  low: { label: 'Low', badge: 'border-black/15 dark:border-white/20 bg-black/[0.03] dark:bg-white/[0.05] text-black/60 dark:text-white/60', bar: 'bg-black/30 dark:bg-white/30' },
};

const DIRECTION_STYLE: Record<InsightDirection, { label: string; badge: string; text: string }> = {
  up: { label: 'Trending up', badge: 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400', text: 'text-amber-700 dark:text-amber-400' },
  down: { label: 'Trending down', badge: 'border-blue-500/30 bg-blue-500/10 text-blue-700 dark:text-blue-400', text: 'text-blue-700 dark:text-blue-400' },
  flat: { label: 'Stable', badge: 'border-black/15 dark:border-white/20 bg-black/[0.03] dark:bg-white/[0.05] text-black/60 dark:text-white/60', text: 'text-current' },
};

function timeAgo(unixSec: number): string {
  const minutes = Math.max(0, Math.round((Date.now() - unixSec * 1000) / 60000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

// horizonSlots counts 15-minute chart-rollup slots (see internal/insight).
function horizonLabel(horizonSlots: number): string {
  const hours = (horizonSlots * 15) / 60;
  return hours < 1 ? `next ${horizonSlots * 15}m` : `next ${hours % 1 === 0 ? hours : hours.toFixed(1)}h`;
}

function fmtValue(v: number): string {
  return Number.isInteger(v) ? String(v) : v.toFixed(2);
}

type Tab = 'overview' | 'anomalies' | 'correlations' | 'predictions';

const TABS: { value: Tab; label: string }[] = [
  { value: 'overview', label: 'Overview' },
  { value: 'anomalies', label: 'Anomalies' },
  { value: 'correlations', label: 'Correlations' },
  { value: 'predictions', label: 'Predictions' },
];

function MetricChip({ children }: { children: string }) {
  return (
    <span className="font-mono text-[13px] px-2.5 py-1 rounded-md bg-black/[0.04] dark:bg-white/[0.06]">{children}</span>
  );
}

function StrengthBar({ value, dim }: { value: number; dim?: boolean }) {
  return (
    <div className="flex items-center gap-2 shrink-0">
      <div className="w-16 h-1.5 rounded-full bg-black/[0.06] dark:bg-white/10 overflow-hidden">
        <div
          className={`h-full rounded-full ${dim ? 'bg-black/30 dark:bg-white/30' : 'bg-blue-500'}`}
          style={{ width: `${Math.min(1, Math.abs(value)) * 100}%` }}
        />
      </div>
      <span className={`font-mono text-xs w-9 text-right ${dim ? 'text-black/50 dark:text-white/50' : 'text-blue-600 dark:text-blue-400'}`}>
        {value.toFixed(2)}
      </span>
    </div>
  );
}

function AnomalyCard({ a }: { a: InsightAnomaly }) {
  const s = SEVERITY_STYLE[a.severity];
  return (
    <AppCard className="flex items-stretch gap-4">
      <span className={`w-1 rounded-full shrink-0 ${s.bar}`} />
      <div className="flex-1 min-w-0 flex flex-col gap-1.5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold leading-none ${s.badge}`}>
            {s.label}
          </span>
          <span className="font-mono text-sm font-medium">{a.type}</span>
        </div>
        <span className="text-[13px] text-black/60 dark:text-white/60">
          {fmtValue(a.value)}, vs a {fmtValue(a.baseline)} baseline · <span className="font-mono">z = {a.z.toFixed(1)}</span>
        </span>
        <span className="text-xs text-black/40 dark:text-white/40">{timeAgo(a.at)}</span>
      </div>
    </AppCard>
  );
}

function CorrelationCard({ c }: { c: InsightCorrelation }) {
  return (
    <AppCard className={`flex flex-col gap-3 ${c.actionable ? '' : 'opacity-70'}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <MetricChip>{c.typeA}</MetricChip>
        <ArrowRight size={16} className="text-black/30 dark:text-white/30 shrink-0" />
        <MetricChip>{c.typeB}</MetricChip>
        <StrengthBar value={c.r} dim={!c.actionable} />
      </div>
      <span className="text-[13px] text-black/60 dark:text-white/60">
        {c.points} shared slots{c.actionable ? '' : ' — below the 0.7 threshold this workspace treats as actionable'}
      </span>
    </AppCard>
  );
}

function PredictionCard({ p }: { p: InsightPrediction }) {
  const s = DIRECTION_STYLE[p.direction];
  return (
    <AppCard className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <span className="font-mono text-sm font-medium">{p.type}</span>
        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold leading-none ${s.badge}`}>
          {s.label}
        </span>
      </div>
      <div className="font-mono text-xl font-semibold">
        {fmtValue(p.current)} <ArrowRight size={16} className="inline -mt-1 text-black/30 dark:text-white/30" />{' '}
        <span className={s.text}>{fmtValue(p.projected)}</span>
      </div>
      <span className="text-xs text-black/40 dark:text-white/40">
        Linear fit, {p.confidence}% confidence · {horizonLabel(p.horizonSlots)}
      </span>
    </AppCard>
  );
}

function OverviewTab({ data }: { data: ChannelInsights }) {
  const anomaly = data.anomalies[0];
  if (!anomaly) {
    return (
      <EmptyState icon={Radar} title="Nothing anomalous" subtitle="No metric is currently outside its own baseline." />
    );
  }
  const correlated = data.correlations.filter((c) => c.typeA === anomaly.type || c.typeB === anomaly.type);
  const forecast = data.predictions.find((p) => p.type === anomaly.type);
  const others = data.anomalies.slice(1);

  return (
    <div className="flex flex-col gap-4">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-black/40 dark:text-white/40">
        Highest-severity thread
      </span>

      <div className="flex flex-col lg:flex-row items-stretch gap-3">
        <AppCard className="flex-1 flex flex-col gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-black/40 dark:text-white/40">
            1 · Anomaly detected
          </span>
          <AnomalyCard a={anomaly} />
        </AppCard>

        <div className="hidden lg:flex items-center justify-center text-black/25 dark:text-white/25">
          <ArrowRight size={20} />
        </div>

        <AppCard className="flex-1 flex flex-col gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-black/40 dark:text-white/40">
            2 · Correlated with
          </span>
          {correlated.length === 0 ? (
            <span className="text-[13px] text-black/40 dark:text-white/40">No correlated metric yet.</span>
          ) : (
            <div className="flex flex-col gap-2">
              {correlated.map((c) => {
                const other = c.typeA === anomaly.type ? c.typeB : c.typeA;
                return (
                  <div key={other} className="flex items-center gap-2">
                    <MetricChip>{other}</MetricChip>
                    <StrengthBar value={c.r} />
                  </div>
                );
              })}
            </div>
          )}
        </AppCard>

        <div className="hidden lg:flex items-center justify-center text-black/25 dark:text-white/25">
          <ArrowRight size={20} />
        </div>

        <AppCard className="flex-1 flex flex-col gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-black/40 dark:text-white/40">
            3 · Predicted to
          </span>
          {forecast ? (
            <PredictionCard p={forecast} />
          ) : (
            <span className="text-[13px] text-black/40 dark:text-white/40">Not enough of a trend to project yet.</span>
          )}
        </AppCard>
      </div>

      {others.length > 0 && (
        <>
          <span className="text-[11px] font-semibold uppercase tracking-wide text-black/40 dark:text-white/40 mt-1">
            Other open threads
          </span>
          <div className="flex flex-col sm:flex-row gap-3">
            {others.map((a) => (
              <AppCard key={a.type} className="flex-1 flex items-center gap-3">
                <span className={`w-1.5 h-8 rounded-full shrink-0 ${SEVERITY_STYLE[a.severity].bar}`} />
                <div className="min-w-0">
                  <span className="font-mono text-sm">{a.type}</span>
                  <span className="text-xs text-black/40 dark:text-white/40 ml-2">
                    {data.predictions.find((p) => p.type === a.type)
                      ? `${DIRECTION_STYLE[data.predictions.find((p) => p.type === a.type)!.direction].label.toLowerCase()}`
                      : 'no trend to project yet'}
                  </span>
                </div>
              </AppCard>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function InsightsScreen() {
  const { channelId } = useParams();
  const { channel, ensureChannelLoaded } = useStore();
  const c = channel(channelId ?? '');
  const [tab, setTab] = useState<Tab>('overview');
  const [data, setData] = useState<ChannelInsights | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (channelId) ensureChannelLoaded(channelId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId]);

  useEffect(() => {
    if (!c) return;
    let cancelled = false;
    setLoading(true);
    fetchChannelInsights(c.nickname).then((result) => {
      if (!cancelled) {
        setData(result);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [c]);

  const empty = !!data && data.anomalies.length === 0 && data.correlations.length === 0 && data.predictions.length === 0;

  return (
    <div className="flex flex-col gap-4 pb-6">
      <ScreenHeader title="Insights" />
      <span className="text-sm text-black/50 dark:text-white/50 -mt-2">
        Anomaly detection, correlations, and predictions for this channel's signals.
      </span>

      <SegmentedControl options={TABS} value={tab} onChange={setTab} />

      {loading || !data ? (
        <span className="text-sm text-black/50 dark:text-white/50 text-center py-8">Loading…</span>
      ) : empty ? (
        <EmptyState
          icon={Radar}
          title="Not enough history yet"
          subtitle="This channel needs more numeric signal history before anomalies, correlations, or trends can be computed."
        />
      ) : (
        <>
          {tab === 'overview' && <OverviewTab data={data} />}

          {tab === 'anomalies' && (
            <div className="flex flex-col gap-3">
              {data.anomalies.length === 0 ? (
                <EmptyState icon={Radar} title="Nothing anomalous" subtitle="No metric is currently outside its own baseline." />
              ) : (
                data.anomalies.map((a) => <AnomalyCard key={a.type} a={a} />)
              )}
            </div>
          )}

          {tab === 'correlations' && (
            <div className="flex flex-col gap-3">
              {data.correlations.length === 0 ? (
                <EmptyState icon={Radar} title="No correlations" subtitle="No metric pair moves together beyond the noise threshold." />
              ) : (
                data.correlations.map((c) => <CorrelationCard key={`${c.typeA}-${c.typeB}`} c={c} />)
              )}
            </div>
          )}

          {tab === 'predictions' && (
            <div className="flex flex-col gap-3">
              {data.predictions.length === 0 ? (
                <EmptyState icon={Radar} title="No trends" subtitle="No metric has a strong enough trend to project yet." />
              ) : (
                data.predictions.map((p) => <PredictionCard key={p.type} p={p} />)
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
